import { Container, isMobileDevice, Loader } from '@volumental/common';
import { sendEvent } from '@volumental/link';
import { type JSX, Suspense, useCallback, useEffect, useMemo } from 'react';
import { lazyWithPreload } from 'react-lazy-with-preload';
import {
  usageTracker,
  UsageTrackerEvent,
  useAnalytics,
  VariantFlag,
} from '@volumental/analytic';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { HistoryFlow } from '../pages/history';
import { SizeChartView } from '../pages/size-chart';
import { trackIfUserDroppedOffSite } from '../services/window-service';
import {
  useInputStore,
  usePersistedViewStore,
  useScannerStore,
  useSessionStore,
  useSurveyStore,
  useViewStore,
} from '../store';
import { AppMode, CurrentView, UserFlowType } from '../types/types';
import { FitSurvey } from '../pages/fit-survey';
import { IntroView } from '../pages/intro/intro-view';
import { ScannerIntro } from '../pages/intro/scanner-intro';
import { AssistedOnboarding } from '../pages/onboarding/assisted-onboarding';
import { SoloOnboarding } from '../pages/onboarding/solo-onboarding';
import { PickerView } from '../pages/picker/picker-view';
import { Scanner } from '../pages/scanner/scanner';
import { ScannerDesktop } from '../pages/scanner/scanner-desktop';
import { ScannerVariant } from '../pages/scanner/utils';
import { graphql } from '../api/gql';
import { PaperFormat } from '../api/gql/graphql';
import { GeneralErrorPage } from '../pages/error/GeneralErrorPage';
import { useExitModals } from './exit-modal-provider/use-exit-modals';
import { ExitModalsProvider } from './exit-modal-provider/exit-modal-provider';
import { FlowNotSupported } from './flow-not-supported';

const Reconstructing = lazyWithPreload(() => import('../pages/reconstructing'));
const Result = lazyWithPreload(() => import('../pages/result'));

const GetOnboardingConfigurationQuery = graphql(`
  query getOnboardingConfigurationQuery {
    configuration {
      scanner {
        allowSelfScanning
        allowAssistedScanning
      }
      features {
        recommendation {
          enableSurvey
        }
      }
    }
  }
`);

const sendCloseEvent = (): void => {
  sendEvent({
    type: 'close',
    data: undefined,
  });
};

export function Navigation(): React.JSX.Element {
  const { loading, error, data } = useQuery(GetOnboardingConfigurationQuery);
  if (loading) return <Loader />;
  if (error || !data) {
    return (
      <GeneralErrorPage
        errorText="Missing Onboarding configuration"
        onClose={sendCloseEvent}
      />
    );
  }

  const {
    scanner: { allowAssistedScanning, allowSelfScanning },
    features: {
      recommendation: { enableSurvey },
    },
  } = data.configuration;
  return (
    <ExitModalsProvider>
      <ViewPicker
        allowAssistedScanning={allowAssistedScanning}
        allowSelfScanning={allowSelfScanning}
        enableSurvey={enableSurvey}
      />
    </ExitModalsProvider>
  );
}

interface ViewPickerProps {
  allowAssistedScanning: boolean;
  allowSelfScanning: boolean;
  enableSurvey: boolean;
}

function ViewPicker({
  allowAssistedScanning,
  allowSelfScanning,
  enableSurvey,
}: ViewPickerProps): JSX.Element {
  const { t } = useTranslation();
  const { isVariantActive } = useAnalytics();
  const { currentView, setCurrentView, setOnboardingCompleted } = useViewStore(
    (store) => store,
  );

  const { exitSurveyHasBeenShown, setExitSurveyHasBeenShown } =
    usePersistedViewStore((store) => store);

  const { clearScanID, scanID, previousPurchaseID, clearPreviousPurchaseID } =
    useInputStore((store) => store);

  const { clearSurveyStore } = useSurveyStore((store) => store);

  const { setFlowType, setScannerVariant, setPaperFormat } = useScannerStore(
    (store) => store,
  );

  const { loadFromQr, paperType, skipOnboarding, mode, resetQrCodeMode } =
    useSessionStore((store) => store);

  const { openSkipToRecom, openExitSurvey } = useExitModals();

  const onClose = useCallback(() => {
    sendCloseEvent();
    if (loadFromQr) {
      resetQrCodeMode();
    }
    if (scanID || previousPurchaseID) {
      setCurrentView(CurrentView.Result);
    } else {
      setCurrentView(CurrentView.Intro);
    }
  }, [scanID, previousPurchaseID]);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent): void => {
      if (event.key === 'Tab') {
        const active = event.target ?? document.activeElement;
        const nodeList = document.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );

        const focusableElements: HTMLElement[] = [...nodeList].filter(
          (element) =>
            element instanceof HTMLElement &&
            element.tagName.toLowerCase() !== 'link' &&
            element.ariaDisabled !== 'true',
        ) as HTMLElement[];

        const firstElement = focusableElements.at(0);
        const lastElement = focusableElements.at(-1);
        if (!firstElement || !lastElement) {
          return;
        }
        if (event.shiftKey && active === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && active === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyPress);

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [currentView, onClose]);

  useEffect(() => {
    trackIfUserDroppedOffSite();
  }, []);

  useEffect(() => {
    if (
      currentView === CurrentView.SoloCapturing ||
      currentView === CurrentView.AssistedCapturing ||
      currentView === CurrentView.SizePicker
    ) {
      void Reconstructing.preload();
    } else if (currentView === CurrentView.Reconstructing) {
      void Result.preload();
    }
  }, [currentView]);

  //Handlers
  const onMobileScanClose = (): void => {
    if (
      exitSurveyHasBeenShown ||
      isVariantActive(VariantFlag.disableExitSurvey)
    ) {
      onClose();
    } else {
      setExitSurveyHasBeenShown(true);
      openExitSurvey({
        onCloseSideBar: onClose,
      });
    }
  };

  const onShowSkipToRecommendationModal = (): void => {
    if (previousPurchaseID) {
      usageTracker.track(UsageTrackerEvent.HybridLeaveOnScannerIntro, {
        sizing_flow_type: UserFlowType.Hybrid,
      });
      openSkipToRecom({
        onSkip: onSkipToRecommandation,
        onCloseExperience: () => {
          usageTracker.track(UsageTrackerEvent.ExitWithoutRecommendation, {
            sizing_flow_type: UserFlowType.Hybrid,
          });
          onMobileScanClose();
        },
      });
    } else {
      onMobileScanClose();
    }
  };

  const onSkipToRecommandation = (): void => {
    usageTracker.track(UsageTrackerEvent.SkipToResultHybrid, {
      sizing_flow_type: UserFlowType.Hybrid,
    });
    setCurrentView(CurrentView.Reconstructing);
    clearSurveyStore();
  };

  const onSkipSurvey = (): void => {
    clearPreviousPurchaseID();
    usageTracker.track(UsageTrackerEvent.SkipFitSurvey);
    setCurrentView(
      currentView === CurrentView.AddPPToScan
        ? CurrentView.Result
        : CurrentView.ScannerIntro,
    );
  };

  const continueToScannerIntro = (): void => {
    clearScanID();
    setCurrentView(CurrentView.ScannerIntro);
  };
  const continueToReconstructing = (): void => {
    setCurrentView(CurrentView.Reconstructing);
    clearSurveyStore();
  };
  const continueToResult = (): void => {
    setCurrentView(CurrentView.Result);
  };
  const backToIntro = (): void => {
    setCurrentView(CurrentView.Intro);
    clearSurveyStore();
  };

  const continueFromScannerPicker = (scannerType: UserFlowType): void => {
    if (scannerType === UserFlowType.Assisted) {
      usageTracker.track(UsageTrackerEvent.PickerLoadAssisted, {
        sizing_flow_type: UserFlowType.Hybrid,
      });
      setScannerVariant(ScannerVariant.Assisted);
      setCurrentView(CurrentView.AssistedOnboarding);
    } else {
      usageTracker.track(UsageTrackerEvent.PickerLoadNormal, {
        sizing_flow_type: UserFlowType.Hybrid,
      });
      setScannerVariant(ScannerVariant.Solo);
      setCurrentView(CurrentView.SoloOnboarding);
    }
  };

  const continueFromPicker = (scannerType: UserFlowType): void => {
    if (scannerType === UserFlowType.Solo) {
      setFlowType(UserFlowType.Solo);
      setScannerVariant(ScannerVariant.Solo);
      usageTracker.track(UsageTrackerEvent.PickerLoadNormal, {
        sizing_flow_type: UserFlowType.Solo,
      });
      setCurrentView(
        isMobileDevice
          ? CurrentView.SoloOnboarding
          : CurrentView.ScannerDesktop,
      );
    } else {
      setFlowType(UserFlowType.Assisted);
      setScannerVariant(ScannerVariant.Assisted);
      usageTracker.track(UsageTrackerEvent.PickerLoadAssisted, {
        sizing_flow_type: UserFlowType.Assisted,
      });
      if (isMobileDevice) {
        if (skipOnboarding && paperType) {
          if (paperType === 'a4') setPaperFormat(PaperFormat.A4);
          else if (paperType === 'us-letter')
            setPaperFormat(PaperFormat.Letter);
          setCurrentView(CurrentView.AssistedCapturing);
        } else {
          setCurrentView(CurrentView.AssistedOnboarding);
        }
      } else {
        setCurrentView(CurrentView.ScannerDesktop);
      }
    }
  };

  const handleCloseQrView = (): void => {
    if (currentView !== CurrentView.ScannerDesktop) {
      setCurrentView(CurrentView.Result);
      return;
    }
    if (enableSurvey) {
      onShowSkipToRecommendationModal();
      return;
    }
    onClose();
  };

  const selectedFlow = useMemo(() => {
    switch (currentView) {
      case CurrentView.HistorySignIn:
      case CurrentView.HistoryValidateAccount:
      case CurrentView.HistorySelectScan: {
        return <HistoryFlow onClose={onClose} />;
      }
      case CurrentView.Intro: {
        return <IntroView onClose={onMobileScanClose} />;
      }
      case CurrentView.AddPPToScan:
      case CurrentView.SizePicker: {
        usageTracker.track(UsageTrackerEvent.HybridSurvey, {
          sizing_flow_type: UserFlowType.Hybrid,
        });
        return (
          <FitSurvey
            onBack={backToIntro}
            onSkip={onSkipSurvey}
            onClose={
              currentView === CurrentView.SizePicker
                ? onMobileScanClose
                : continueToResult
            }
            onContinue={
              currentView === CurrentView.SizePicker
                ? continueToScannerIntro
                : continueToReconstructing
            }
            submitButtonText={t('button.continue')}
          />
        );
      }

      case CurrentView.ScannerIntro: {
        usageTracker.track(UsageTrackerEvent.ScannerIntro, {
          sizing_flow_type: UserFlowType.Hybrid,
        });
        return (
          <ScannerIntro
            onBack={() => {
              setCurrentView(CurrentView.SizePicker);
            }}
            onClose={onShowSkipToRecommendationModal}
            onContinue={() => {
              setCurrentView(
                isMobileDevice
                  ? CurrentView.ScannerPicker
                  : CurrentView.ScannerDesktop,
              );
            }}
            onSkip={previousPurchaseID ? onSkipToRecommandation : undefined}
          />
        );
      }
      case CurrentView.RecomQRcodeView:
      case CurrentView.ScannerDesktop: {
        return (
          <ScannerDesktop
            onBack={
              currentView === CurrentView.ScannerDesktop
                ? () => {
                    setCurrentView(
                      enableSurvey && mode === AppMode.Recommendation
                        ? CurrentView.ScannerIntro
                        : CurrentView.Intro,
                    );
                  }
                : undefined
            }
            onClose={handleCloseQrView}
          />
        );
      }
      case CurrentView.Picker:
      case CurrentView.ScannerPicker: {
        return (
          <PickerView
            onBack={
              loadFromQr
                ? undefined
                : () => {
                    setCurrentView(
                      currentView === CurrentView.Picker
                        ? CurrentView.Intro
                        : CurrentView.ScannerIntro,
                    );
                  }
            }
            onClose={
              currentView === CurrentView.Picker
                ? onMobileScanClose
                : onShowSkipToRecommendationModal
            }
            onContinue={
              currentView === CurrentView.Picker
                ? continueFromPicker
                : continueFromScannerPicker
            }
            title={
              currentView === CurrentView.Picker
                ? t('multiIntro.title')
                : t('hybrid.scannerPickerTitle')
            }
          />
        );
      }

      case CurrentView.SoloOnboarding: {
        return (
          <SoloOnboarding
            onBack={() => {
              if (enableSurvey && mode === AppMode.Recommendation) {
                setCurrentView(CurrentView.ScannerPicker);
              } else if (allowSelfScanning && !allowAssistedScanning) {
                // this only check if we have solo on
                usageTracker.track(UsageTrackerEvent.BackToIntro, { mode });
                setCurrentView(CurrentView.Intro);
              } else {
                usageTracker.track(UsageTrackerEvent.BackToPicker, { mode });
                setOnboardingCompleted(false);
                setCurrentView(CurrentView.Picker);
              }
            }}
            onClose={
              enableSurvey ? onShowSkipToRecommendationModal : onMobileScanClose
            }
            onNext={() => {
              usageTracker.track(UsageTrackerEvent.OnboardingCompleted, {
                sizing_flow_type: enableSurvey
                  ? UserFlowType.Hybrid
                  : UserFlowType.Solo,
                scanner_type: 'solo',
              });
              setCurrentView(CurrentView.SoloCapturing);
            }}
          />
        );
      }

      case CurrentView.AssistedOnboarding: {
        return (
          <AssistedOnboarding
            onBack={() => {
              setCurrentView(
                enableSurvey && mode === AppMode.Recommendation
                  ? CurrentView.ScannerPicker
                  : CurrentView.Picker,
              );
            }}
            onClose={
              enableSurvey ? onShowSkipToRecommendationModal : onMobileScanClose
            }
            onNext={() => {
              setCurrentView(CurrentView.AssistedCapturing);
              usageTracker.track(UsageTrackerEvent.OnboardingCompleted, {
                sizing_flow_type: enableSurvey
                  ? UserFlowType.Hybrid
                  : UserFlowType.Assisted,
                scanner_type: 'assisted',
              });
            }}
          />
        );
      }
      case CurrentView.AssistedCapturing:
      case CurrentView.SoloCapturing: {
        return (
          <Scanner
            onClose={onShowSkipToRecommendationModal}
            onBackToOnboarding={() => {
              setOnboardingCompleted(true);
              setCurrentView(
                currentView === CurrentView.SoloCapturing
                  ? CurrentView.SoloOnboarding
                  : CurrentView.AssistedOnboarding,
              );
            }}
            scannerVariant={
              currentView === CurrentView.SoloCapturing
                ? ScannerVariant.Solo
                : ScannerVariant.Assisted
            }
          />
        );
      }
      case CurrentView.Reconstructing: {
        return (
          <Suspense fallback={<Loader />}>
            <Reconstructing />
          </Suspense>
        );
      }

      case CurrentView.Result: {
        return (
          <Suspense fallback={<Loader />}>
            <Result onClose={onClose} />
          </Suspense>
        );
      }
      case CurrentView.SizeChart: {
        return <SizeChartView onClose={onClose} />;
      }
      case undefined: {
        return <FlowNotSupported />;
      }
    }
  }, [currentView, exitSurveyHasBeenShown]);

  return <Container>{selectedFlow}</Container>;
}
